
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';
import { trackPhoneCallConversion } from '@/utils/trackPhoneCall';

const BuddingeLocation = () => {
  const testimonials = [
    {
      name: "Camilla Nielsen",
      location: "Buddinge",
      text: "Oscar er en fantastisk lærer! Hans tålmodighed og evne til at forklare selv komplekse musikalske begreber på en enkel måde har gjort det muligt for mig at udvikle mig langt hurtigere end jeg havde forventet.",
      stars: 5
    },
    {
      name: "Henrik Petersen",
      location: "Buddinge",
      text: "Min søn på 8 år elsker at gå til klaverundervisning hos Oscar. Han formår at gøre timerne sjove og engagerende, hvilket er afgørende for at fastholde et barns interesse.",
      stars: 5
    },
    {
      name: "Mette Jørgensen",
      location: "Buddinge",
      text: "Efter at have holdt en lang pause med klaveret, var jeg nervøs for at starte igen. Oscar har været utrolig opmuntrende og skabt et trygt miljø, hvor jeg har kunnet genopbygge min selvtillid ved instrumentet.",
      stars: 5
    }
  ];

  const features = [
    {
      title: "Tæt på Buddinge",
      description: "Klaverundervisning i Søborg, tæt på Buddinge og med nem adgang til offentlig transport.",
      icon: "map-pin"
    },
    {
      title: "Alle aldersgrupper",
      description: "Undervisning for både børn (fra 6 år), unge og voksne i alle aldre.",
      icon: "users"
    },
    {
      title: "Alle niveauer",
      description: "Fra nybegyndere til øvede pianister - alle er velkomne uanset niveau.",
      icon: "graduation-cap"
    },
    {
      title: "Skræddersyet undervisning",
      description: "Undervisningen tilpasses dine personlige mål, niveau og musikalske interesser.",
      icon: "music"
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Buddinge | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Buddinge med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Buddinge"
        description="Få professionel klaverundervisning med Oscar Gilbert, nemt tilgængeligt for elever fra Buddinge. Undervisning for alle niveauer og aldre."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning for <span className="text-piano-gold">Buddinge</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Oscar Gilbert tilbyder klaverundervisning af høj kvalitet til elever fra Buddinge. Med sin baggrund fra Rytmisk Musikkonservatorium og mange års erfaring som klaverlærer, sikrer Oscar en professionel og inspirerende undervisning.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Uanset om du drømmer om at spille dine yndlingssange, forbedre din teknik eller måske forberede dig til en optagelsesprøve, kan Oscar hjælpe dig med at nå dine mål ved klaveret.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Undervisningen foregår i Søborg, kun få minutter fra Buddinge, i lyse og behagelige lokaler med professionelt udstyr.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book gratis prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Buddinge" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                    width="400"
                    height="400"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Buddinge" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, tæt på Buddinge.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12607.679143124358!2d12.514096630724325!3d55.734728901797794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743668909093!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Buddinge:</strong></p>
                    <p className="text-gray-700">• God busforbindelse med bus 6A fra Buddinge Station</p>
                    <p className="text-gray-700">• Kort gåafstand fra flere busstoppesteder</p>
                    <p className="text-gray-700">• Gratis parkering ved undervisningslokalet</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline" onClick={trackPhoneCallConversion}>+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Buddinge" />
    </Layout>
  );
};

export default BuddingeLocation;
